import type { CartAmountsStore } from "./types";
import type { AnyAction } from "../../../redux/types";
import type { TransformedCartData } from "~/feature/cart/cart/transformers/CartViewDataTransformer";
import { type LineItem, PackageEntrySource } from "~/feature/cart/cart/models/CartData";
import { CART_LOAD_SUCCESS } from "~/feature/cart/cart/redux/actions";
import {
  get13DigitCodeForCartAmounts,
  get15DigitCodeForCartAmounts,
} from "~/AddToCart/redux/cartAmounts/cartAmountsHelper";
import { CLEAR_CART, SET_PACKAGE_ENTRIES } from "~/feature/cart/cart/redux/actions/actionTypes";
import { ADD_QTY, LOAD_CART_NONCART_PAGE } from "~/AddToCart/redux/cartAmounts/actionTypes";

export const cartAmountsInitialState: CartAmountsStore = {
  cartQtyPerProductCode: {},
  loaded: false,
  loading: false,
};

interface TransformResults {
  qtyPerProductCode: { [code: string]: number };
}
function transformCartData(data: TransformedCartData): TransformResults {
  const qtyPerProductCode: Dict<number> = {};
  Object.values(data.packEntriesById).forEach((packEntry) => {
    const productCode = packEntry.productCode;
    const { packCode, priceReference } = packEntry;
    const code13Digit = get13DigitCodeForCartAmounts(productCode, priceReference);
    const code15Digit = get15DigitCodeForCartAmounts(productCode, packCode, priceReference);
    if (packEntry.source === PackageEntrySource.CONTRACT) {
      // Don't do anything
    } else if (packEntry.source === PackageEntrySource.DEFAULT || packEntry.source === PackageEntrySource.BULK) {
      let currQty = qtyPerProductCode[code13Digit] || 0;
      qtyPerProductCode[code13Digit] = currQty + packEntry.quantity;
      currQty = qtyPerProductCode[code15Digit] || 0;
      qtyPerProductCode[code15Digit] = currQty + packEntry.quantity;
    }
  });
  return { qtyPerProductCode };
}

// Reducer that adds data from actions native to other modules
export const cartAmountsReducer = (state = cartAmountsInitialState, action: AnyAction): CartAmountsStore => {
  switch (action.type) {
    case LOAD_CART_NONCART_PAGE:
    case CART_LOAD_SUCCESS: {
      const data = action.payload as TransformedCartData;
      const { qtyPerProductCode } = transformCartData(data);

      return {
        ...state,
        cartQtyPerProductCode: qtyPerProductCode,
        loaded: true,
      };
    }

    case ADD_QTY: {
      const { productCode, packCode, amount, priceReference } = action.payload as {
        productCode: string;
        packCode: string;
        amount: number;
        priceReference: string;
      };
      const code13Digit = get13DigitCodeForCartAmounts(productCode, priceReference);
      const code15Digit = get15DigitCodeForCartAmounts(productCode, packCode, priceReference);

      return {
        ...state,
        cartQtyPerProductCode: {
          ...state.cartQtyPerProductCode,
          [code13Digit]: (state.cartQtyPerProductCode[code13Digit] || 0) + amount,
          [code15Digit]: (state.cartQtyPerProductCode[code15Digit] || 0) + amount,
        },
      };
    }

    case SET_PACKAGE_ENTRIES: {
      const data = action.payload as {
        packageEntry: LineItem;
        oldPackageEntry?: LineItem;
        productCode: string;
      }[];
      const cartQtyPerProductCode = { ...state.cartQtyPerProductCode };

      data.forEach(({ packageEntry, oldPackageEntry, productCode }) => {
        if (oldPackageEntry) {
          const old13Digit = get13DigitCodeFromLineItem(oldPackageEntry, productCode);
          const old15Digit = get15DigitCodeFromLineItem(oldPackageEntry, productCode);
          addQty(cartQtyPerProductCode, -oldPackageEntry.quantity, old13Digit, old15Digit);
        }

        const new13Digit = get13DigitCodeFromLineItem(packageEntry, productCode);
        const new15Digit = get15DigitCodeFromLineItem(packageEntry, productCode);
        addQty(cartQtyPerProductCode, packageEntry.quantity, new13Digit, new15Digit);
      });

      return {
        ...state,
        cartQtyPerProductCode,
      };
    }

    case CLEAR_CART: {
      return {
        ...state,
        cartQtyPerProductCode: {},
      };
    }

    default:
      return state;
  }
};

const addQty = (qtyMap: Dict<number | undefined>, qty: number, ...codes: string[]) => {
  codes.forEach((code) => (qtyMap[code] = (qtyMap[code] || 0) + qty));
};

const get13DigitCodeFromLineItem = ({ priceReference }: LineItem, productCode: string) => {
  return get13DigitCodeForCartAmounts(productCode, priceReference);
};

const get15DigitCodeFromLineItem = ({ priceReference, packCode }: LineItem, productCode: string) => {
  return get15DigitCodeForCartAmounts(productCode, packCode, priceReference);
};
